import {
  requestNearbyHikesInCategory,
  requestNearbyOffroadRoutesInCategory,
} from '@/lib/quick-search/queries'
import {
  QuickSearchEntityType,
  type QuickSearchFilter,
  type QuickSearchResult,
  type QuickSearchConfig,
} from '@/lib/quick-search/types'
import QuickSearchProvider from '@/lib/quick-search/provider'
import { createSearchResultMarker } from '@/lib/search/search-utils'
import subscriptionService from '@/services/subscription-service'
import {
  clearAllMarkers,
  replaceMarkers,
  setSearchPinSelected,
} from '@/services/map-service'
import { isSearchPinsV2Enabled } from '@/services/search-service'
import {
  clearSearchResultPins,
  displaySearchResultPins,
  generatePinFeatureFromSearchResult,
} from '@/lib/search/search-result-pins'

const isUserEntitledForQuickSearch = () => !subscriptionService.isBasic()

/** If the Quick Search feature is enabled or not */
export const isQuickSearchEnabled = () => isUserEntitledForQuickSearch()

/** Get the Quick Search categories for the current app and activity mode. */
export const getQuickSearchConfig = (): QuickSearchConfig | null => {
  return QuickSearchProvider.getConfig()
}

/**
 * A mapping of QuickSearchEntityTypes to the glyph icon name used
 * in the search result and map marker for that entity type
 */
export const quickSearchEntityTypeGlyphMap = {
  [QuickSearchEntityType.HikeRoute]: 'HIKE_BACKPACK',
  [QuickSearchEntityType.OffroadRoute]: 'OVERLAND',
}

/**
 * Execute a Quick Search query, based upon a given entity type and filter.
 * This maps each QuickSearchEntityType to a specifc data request function defined in the quick search queries file
 */
export const makeQuickSearchRequest = async (
  entityType: QuickSearchEntityType,
  filters: QuickSearchFilter,
  enforceBbox = false
) => {
  let fetchedResults: QuickSearchResult[] = []

  if (entityType === QuickSearchEntityType.HikeRoute) {
    fetchedResults = await requestNearbyHikesInCategory(filters, enforceBbox)
  } else if (entityType === QuickSearchEntityType.OffroadRoute) {
    fetchedResults = await requestNearbyOffroadRoutesInCategory(
      filters,
      enforceBbox
    )
  }

  return fetchedResults
}

/**
 * Add a set of quick search results to the map in the form of search map marker components
 */
export const addQuickSearchResultsToMap = (
  results: QuickSearchResult[],
  entityType: QuickSearchEntityType
) => {
  if (isSearchPinsV2Enabled()) {
    const pinFeatures = results.map((result, index) => {
      return generatePinFeatureFromSearchResult({
        id: result.id,
        name: result.name,
        type: quickSearchEntityTypeGlyphMap[entityType],
        location: result.location.coordinates,
        index,
      })
    })
    displaySearchResultPins(pinFeatures)
    return
  }

  const searchResultMarkers = results.map((result) => {
    const resultGlyph = quickSearchEntityTypeGlyphMap[entityType]

    return createSearchResultMarker(result.id, resultGlyph, result.location)
  })

  replaceMarkers(searchResultMarkers)
}

/**
 * Add the marker for a selected quick search result on the map
 */
export const addSelectedQuickSearchMarkerToMap = (
  result: QuickSearchResult,
  entityType: QuickSearchEntityType
) => {
  if (isSearchPinsV2Enabled()) {
    const pinFeature = generatePinFeatureFromSearchResult({
      id: result.id,
      name: result.name,
      type: quickSearchEntityTypeGlyphMap[entityType],
      location: result.location.coordinates,
      index: 0,
    })
    displaySearchResultPins([pinFeature])

    setSearchPinSelected(result.id)
  }
}

/**
 * Remove all quick search results from the map
 */
export const removeQuickSearchResultsFromMap = () => {
  if (isSearchPinsV2Enabled()) {
    clearSearchResultPins()
    return
  }

  clearAllMarkers()
}
